/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { SLICE } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import { signInActions } from 'modules/sign-in/slice';

/**
 * Define the the users feature initial state
 */
export const initialState = {
  loading: false,
  usersList: {},
  roleList: {},
  hotelsList: {},
  hotelSelectionList: {},
  updatedUser: {},
  currentUser: {},
  landingPage: {},
  isLandingPageUpdated: false,
  stayDatesPresetList: [],
  currencyData: [],
  isOtpSent: false,
  isPhoneNumberVerified: false,
  advancedQueryInterpreter: {},
  consent: {},
  interpreterAgent: {},
  interpreterAgentErrorMessage : ''
};
/**
 * Here mentioned all the users feature related actions
 */
export const usersSlice = createSlice({
  name: SLICE.USERS,
  initialState,
  reducers: {
    usersList(state) {
      state.loading = true;
    },
    usersListSucceeded(state, action) {
      state.loading = false;
      state.usersList = action?.payload;
    },
    usersListFailed(state) {
      state.loading = false;
    },
    createAdminUsers(state) {
      state.loading = true;
    },
    createAdminUsersSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = [...state.usersList.results, action?.payload];
      state.updatedUser = action?.payload;
    },
    createAdminUsersFailed(state) {
      state.loading = false;
    },
    createGeneralUsers(state) {
      state.loading = true;
    },
    createGeneralUsersSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = [...state.usersList.results, action?.payload];
      state.updatedUser = action?.payload;
    },
    createGeneralUsersFailed(state) {
      state.loading = false;
    },
    createUsers(state) {
      state.loading = true;
    },
    createUsersSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = [...state.usersList.results, action?.payload];
      state.updatedUser = action?.payload;
    },
    createUsersFailed(state) {
      state.loading = false;
    },
    updateUser(state) {
      state.loading = true;
    },
    updateUserSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = state?.usersList?.results?.map((object) =>
        object?.id === action?.payload?.id ? { ...object, ...action.payload } : object
      );
    },
    updateUserFailed(state) {
      state.loading = false;
    },
    updateRole(state) {
      state.loading = true;
    },
    updateRoleSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = state?.usersList?.results?.map((object) =>
        object?.id === action?.payload?.id ? { ...object, ...action.payload } : object
      );
    },
    updateRoleFailed(state) {
      state.loading = false;
    },
    getRoles(state) {
      state.loading = true;
    },
    getRolesSucceeded(state, action) {
      state.loading = false;
      state.roleList = action?.payload?.results;
    },
    getRolesFailed(state) {
      state.loading = false;
    },
    deleteUser(state) {
      state.loading = true;
    },
    deleteUserSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = state?.usersList?.results.filter(
        (object) => object?.id !== action?.payload?.data?.userId
      );
    },
    deleteUserFailed(state) {
      state.loading = false;
    },
    getHotelsList(state) {
      state.loading = true;
    },
    getHotelsListSucceeded(state, action) {
      state.loading = false;
      state.hotelsList = action?.payload;
    },
    getHotelsListFailed(state) {
      state.loading = false;
    },
    getSelectionHotelsList(state) {
      state.loading = true;
    },
    getSelectionHotelsListSucceeded(state, action) {
      state.loading = false;
      state.hotelSelectionList = action?.payload;
    },
    getSelectionHotelsListFailed(state) {
      state.loading = false;
    },
    updateHotelsList(state) {
      state.loading = true;
    },
    updateHotelsListSucceeded(state) {
      state.loading = false;
      state.updatedUser = initialState.updatedUser;
    },
    updateHotelsListFailed(state) {
      state.loading = false;
    },
    getUserById(state) {
      state.loading = true;
    },
    getUserByIdSucceeded(state, action) {
      state.loading = false;
      state.currentUser = action?.payload;
    },
    getUserByIdFailed(state) {
      state.loading = false;
    },
    changeUserPassword(state) {
      state.loading = true;
    },
    changeUserPasswordSucceeded(state) {
      state.loading = false;
    },
    changeUserPasswordFailed(state) {
      state.loading = false;
    },
    verifyOtp(state) {
      state.loading = true;
    },
    verifyOtpSucceeded(state, action) {
      state.loading = false;
      if (action.payload?.message === 'OTP verified successfully') {
        state.isPhoneNumberVerified = true;
      }
      state.isOtpSent = false;
    },
    verifyOtpFailed(state) {
      state.loading = false;
      state.isPhoneNumberVerified = false;
    },
    updatePreference(state) {
      state.loading = true;
    },
    updatePreferenceSucceeded(state, action) {
      state.loading = false;
      state.usersList.results = state?.usersList?.results?.map((object) =>
        object?.id === action?.payload?.id ? { ...object, ...action.payload } : object
      );
      state.isOtpSent =
        action.payload?.phoneNumber && action.payload?.isPhoneNumberVerified === false;
    },
    updatePreferenceFailed(state) {
      state.loading = false;
    },
    updateLandingPage(state) {
      state.loading = true;
    },
    updateLandingPageSucceeded(state, action) {
      state.loading = false;
      state.landingPage = action?.payload;
      state.isLandingPageUpdated = true;
    },
    updateLandingPageFailed(state) {
      state.loading = false;
    },
    getLandingPage(state) {
      state.loading = true;
    },
    getLandingPageSucceeded(state, action) {
      state.loading = false;
      state.landingPage = action?.payload;
      state.isLandingPageUpdated = false;
    },
    getLandingPageFailed(state) {
      state.loading = false;
    },
    saveStayDatesPreset(state) {
      state.loading = true;
    },
    saveStayDatesPresetSucceeded(state, action) {
      state.loading = false;
      state.stayDatesPresetList = action?.payload?.customDataRanges;
    },
    saveStayDatesPresetFailed(state) {
      state.loading = false;
    },
    getStayDatesPresets(state) {
      state.loading = true;
    },
    getStayDatesPresetsSucceeded(state, action) {
      state.advancedQueryInterpreter = action?.payload?.advancedQueryInterpreter;
      state.consent = action?.payload?.consent;
      state.interpreterAgent = action?.payload?.interpreterAgent;
      state.loading = false;
      state.stayDatesPresetList = action?.payload?.customDataRanges;
    },
    getStayDatesPresetsFailed(state) {
      state.loading = false;
    },
    deleteStayDatesPreset(state) {
      state.loading = true;
    },
    deleteStayDatesPresetSucceeded(state, action) {
      state.loading = false;
      state.stayDatesPresetList = action?.payload?.customDataRanges;
    },
    deleteStayDatesPresetFailed(state) {
      state.loading = false;
    },
    updateHotelMetadata(state) {
      state.loading = true;
    },
    updateHotelMetadataSucceeded(state) {
      state.loading = false;
    },
    updateHotelMetadataFailed(state) {
      state.loading = false;
    },
    getCurrencyData(state) {
      state.loading = true;
    },
    getCurrencyDataSucceeded(state, action) {
      state.loading = false;
      state.currencyData = action?.payload.results;
    },
    getCurrencyDataFailed(state) {
      state.loading = false;
    },
    updateUserConfiguration(state) {
      state.loading = true;
    },
    updateUserConfigurationSucceeded(state, action) {
      state.loading = false;
      state.advancedQueryInterpreter = action?.payload?.advancedQueryInterpreter;
      state.consent = action?.payload?.consent;
      state.interpreterAgent = action?.payload?.interpreterAgent
    },
    updateUserConfigurationFailed(state) {
      state.loading = false;
    },
    setInterpreterAgentMessage(state, action) {
      state.interpreterAgentErrorMessage = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(dashboardActions.getAdvancedQueryLLMInSucceeded, (state, action) => ({
        ...state,
        advancedQueryInterpreter: action?.payload?.tokenUsage,
      }))
      .addCase(dashboardActions.exportVisualDataSucceeded, (state, action) => ({
        ...state,
        interpreterAgent: action?.payload?.response?.tokenUsage,
      }))
      .addCase(dashboardActions.exportVisualDataFailed, (state, action) => ({
        ...state,
        interpreterAgentErrorMessage: action?.payload?.error?.message ?? 'Something went wrong.',
      }))
      .addCase(dashboardActions.getUserViResponseByShareIdFailed, (state, action) => ({
        ...state,
        interpreterAgentErrorMessage: action?.payload?.message ?? 'Something went wrong.',
      }))
      .addCase(dashboardActions.getProgressNotificationsListSucceeded, (state, action) => {
        const imageData = action.payload.results?.filter(
          (ll) => ll?.payload?.type === 'LLM' && ll.isOpen === false
        );

        const errorData = action.payload.results?.filter(
          (ll) => ll?.payload?.type === 'ERROR' && ll.isOpen === false
        );
        return {
          ...state,
          interpreterAgentErrorMessage:
            errorData?.length > 0 ? errorData?.[0]?.payload?.response : state.interpreterAgentErrorMessage,
          interpreterAgent:
            imageData?.length > 0
              ? imageData?.[0]?.payload?.response?.tokenUsage
              : state.interpreterAgent,
        };
      })
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(signInActions.signInSucceeded, (state, action) => ({
        ...state,
        isPhoneNumberVerified: action?.payload?.user?.phoneNumber?.isVerified ?? false,
      }));
  },
});
//
export const { actions: usersActions } = usersSlice;
